import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { registerModalBackHandler } from '../../lib/native';
import { HostCitiesEditor } from '../../components/admin/HostCitiesEditor';
import { RolesDialog } from '../../components/admin/RolesDialog';
import { useAuth } from '../../hooks/useAuth';
import { describeAudit, lastSeenLabel, MAX_TAGS_PER_PERSON, parseTags, userAlerts, type AuditEntry, type DirectoryUser } from '../../lib/adminUsers';
import type { UserRole } from '../../types';
import {
  Button,
  Spinner,
  StatusBadge,
} from '../../components/design-system';
import type { StatusTone } from '../../lib/statusChip';

interface UserDetailModalProps {
  userId: string;
  onClose: () => void;
  /** Called after this panel changes something the list shows (roles). */
  onChanged?: () => void;
}

interface UserData {
  profile: Record<string, unknown>;
  hostProfile: Record<string, unknown> | null;
  stripeConnect: Record<string, unknown> | null;
  availability: Array<Record<string, unknown>>;
  conversations: Array<Record<string, unknown>>;
  portraits: Array<Record<string, unknown>>;
  matches: Array<Record<string, unknown>>;
  payments: Array<Record<string, unknown>>;
  summary: DirectoryUser | null;
}

function Section({ title, children, empty }: { title: string; children: React.ReactNode; empty?: boolean }) {
  if (empty) return null;
  return (
    <div className="mb-5">
      <h3 className="text-xs font-semibold text-app-muted uppercase tracking-wide mb-2">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode | unknown }) {
  const isEmpty = value == null || value === '';
  const node = isEmpty
    ? <span className="text-app-muted italic">—</span>
    : (typeof value === 'object' && value !== null ? (value as React.ReactNode) : String(value));
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] text-app-muted uppercase tracking-wide">{label}</span>
      <span className="text-sm text-app-fg break-all">{node}</span>
    </div>
  );
}

const COLOR_TONE: Record<'green' | 'red' | 'yellow' | 'gray', StatusTone> = {
  green: 'success',
  red: 'crimson',
  yellow: 'gold',
  gray: 'neutral',
};

function Badge({ color, children }: { color: 'green' | 'red' | 'yellow' | 'gray'; children: React.ReactNode }) {
  return <StatusBadge tone={COLOR_TONE[color]} label={children as string} />;
}

function statusColor(status: string): 'green' | 'red' | 'yellow' | 'gray' {
  if (['paid', 'accepted', 'completed', 'complete'].includes(status)) return 'green';
  if (['failed', 'declined', 'refunded'].includes(status)) return 'red';
  if (['pending', 'proposed', 'active'].includes(status)) return 'yellow';
  return 'gray';
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function centsToEur(cents: unknown): string {
  if (typeof cents !== 'number') return '—';
  return `€${(cents / 100).toFixed(2)}`;
}

type TabKey = 'overview' | 'trips' | 'payments' | 'host' | 'chats' | 'notes' | 'activity';

interface Note { id: string; body: string; created_at: string; author_id: string; author_name: string | null }

function Tile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-app-bg border border-app-border px-3 py-2.5">
      <p className="text-[10px] text-app-muted uppercase tracking-wide">{label}</p>
      <p className="text-base font-semibold text-app-fg mt-0.5">{value}</p>
    </div>
  );
}

export function UserDetailModal({ userId, onClose, onChanged }: UserDetailModalProps) {
  const { user: me } = useAuth();
  const [data, setData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>('overview');
  const [reload, setReload] = useState(0);
  const [editingRoles, setEditingRoles] = useState(false);
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [actionNote, setActionNote] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [noteText, setNoteText] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [tagText, setTagText] = useState('');
  const [tagError, setTagError] = useState<string | null>(null);

  // Android hardware back button closes this panel instead of navigating
  // the route underneath it.
  useEffect(() => registerModalBackHandler(onClose), [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const [
        { data: profile },
        { data: hostProfile },
        { data: stripeConnect },
        { data: availability },
        { data: conversations },
        { data: portraits },
        { data: matchesAsTraveler },
        { data: matchesAsHost },
        { data: paymentsAsTraveler },
        { data: paymentsAsHost },
        { data: summaryRows },
      ] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', userId).single(),
        supabase.from('host_profiles').select('*').eq('profile_id', userId).maybeSingle(),
        supabase.from('stripe_connect_accounts').select('*').eq('host_id', userId).maybeSingle(),
        supabase.from('host_unavailability').select('*').eq('host_id', userId).order('start_date'),
        supabase.from('conversations').select('id, status, created_at, updated_at').eq('traveler_id', userId).order('created_at', { ascending: false }),
        supabase.from('taste_portraits').select('id, portrait_json, host_summary, trip_days, start_date, created_at').eq('traveler_id', userId).order('created_at', { ascending: false }),
        supabase.from('matches').select('id, host_id, status, rank, match_note, created_at, profiles!matches_host_id_fkey(full_name)').eq('traveler_id', userId).order('created_at', { ascending: false }),
        supabase.from('matches').select('id, traveler_id, status, rank, match_note, created_at, profiles!matches_traveler_id_fkey(full_name)').eq('host_id', userId).order('created_at', { ascending: false }),
        supabase.from('payments').select('*').eq('traveler_id', userId).order('created_at', { ascending: false }),
        supabase.from('payments').select('*').eq('host_id', userId).order('created_at', { ascending: false }),
        supabase.rpc('admin_list_users', { p_search: userId, p_limit: 1 }),
      ]);

      if (cancelled) return;

      const allMatches = [
        ...(matchesAsTraveler ?? []).map((m) => ({ ...m, _role: 'traveler' })),
        ...(matchesAsHost ?? []).map((m) => ({ ...m, _role: 'host' })),
      ].sort((a, b) => new Date(b.created_at as string).getTime() - new Date(a.created_at as string).getTime());

      const allPayments = [
        ...(paymentsAsTraveler ?? []).map((p) => ({ ...p, _role: 'traveler' })),
        ...(paymentsAsHost ?? []).map((p) => ({ ...p, _role: 'host' })),
      ].sort((a, b) => new Date(b.created_at as string).getTime() - new Date(a.created_at as string).getTime());

      setData({
        profile: profile ?? {},
        hostProfile: hostProfile ?? null,
        stripeConnect: stripeConnect ?? null,
        availability: (availability ?? []) as Array<Record<string, unknown>>,
        conversations: (conversations ?? []) as Array<Record<string, unknown>>,
        portraits: (portraits ?? []) as Array<Record<string, unknown>>,
        matches: allMatches as Array<Record<string, unknown>>,
        payments: allPayments as Array<Record<string, unknown>>,
        summary: ((summaryRows as DirectoryUser[] | null) ?? [])[0] ?? null,
      });
      setLoading(false);
    }

    void load();
    return () => { cancelled = true; };
  }, [userId, reload]);

  // The Activity tab loads when it is opened and again after something changes.
  useEffect(() => {
    if (tab !== 'activity') return;
    let cancelled = false;
    void supabase.rpc('admin_user_audit', { p_user_id: userId, p_limit: 50 }).then(({ data: rows }) => {
      if (!cancelled) setAudit((rows as AuditEntry[] | null) ?? []);
    });
    return () => { cancelled = true; };
  }, [tab, userId, reload]);

  useEffect(() => {
    if (tab !== 'notes') return;
    let cancelled = false;
    void supabase.rpc('admin_user_notes_list', { p_user_id: userId }).then(({ data: rows }) => {
      if (!cancelled) setNotes((rows as Note[] | null) ?? []);
    });
    return () => { cancelled = true; };
  }, [tab, userId, reload]);

  async function addNote() {
    setNoteBusy(true);
    setNoteError(null);
    const { error } = await supabase.rpc('admin_add_user_note', { p_user_id: userId, p_body: noteText });
    setNoteBusy(false);
    if (error) { setNoteError(error.message || "We couldn't save the note."); return; }
    setNoteText('');
    setReload((n) => n + 1);
  }

  async function deleteNote(id: string) {
    setNoteBusy(true);
    const { error } = await supabase.rpc('admin_delete_user_note', { p_note_id: id });
    setNoteBusy(false);
    if (error) { setNoteError(error.message || "We couldn't delete the note."); return; }
    setReload((n) => n + 1);
  }

  async function changeTags(add: string[], remove: string[]) {
    setTagError(null);
    const { error } = await supabase.rpc('admin_set_user_tags', { p_user_ids: [userId], p_add: add, p_remove: remove });
    if (error) { setTagError(error.message || "We couldn't change the tags."); return; }
    setTagText('');
    setReload((n) => n + 1);
    onChanged?.();
  }

  function submitTags() {
    const { tags, invalid } = parseTags(tagText);
    if (invalid.length > 0) { setTagError(`Not allowed: ${invalid.join(', ')}`); return; }
    if (tags.length > 0) void changeTags(tags, []);
  }

  async function runAction(action: 'password_reset' | 'resend_confirmation' | 'reset_2fa') {
    setActing(action);
    setActionNote(null);
    const { data: res, error } = await supabase.functions.invoke('admin-user-actions', { body: { action, user_id: userId } });
    setActing(null);
    // A refusal arrives as an error; its message is in the response body.
    let message = (res as { error?: string } | null)?.error;
    if (error && !message) {
      try { message = ((await (error as { context?: Response }).context?.json()) as { error?: string })?.error; } catch { /* keep the default */ }
    }
    if (error || message) {
      setActionNote({ tone: 'bad', text: message || "We couldn't do that. Try again in a moment." });
      return;
    }
    setActionNote({ tone: 'ok', text: action === 'password_reset' ? 'Password-reset email sent.' : action === 'reset_2fa' ? 'Two-factor authentication reset. They were told by email.' : 'Confirmation email sent.' });
    setConfirmReset(false);
    setReload((n) => n + 1);
    if (action === 'reset_2fa') onChanged?.();
  }

  const p = data?.profile;
  const sum = data?.summary ?? null;
  const alerts = data ? userAlerts({
    profile: data.profile as { deleted_at?: unknown; terms_accepted_at?: unknown },
    hostProfile: data.hostProfile,
    stripeConnect: data.stripeConnect,
  }) : [];
  const roles = ((p?.roles as string[] | undefined) ?? [p?.role as string]).filter(Boolean);
  const tabs: { key: TabKey; label: string; count?: number }[] = data ? [
    { key: 'overview', label: 'Overview' },
    { key: 'trips', label: 'Trips', count: data.matches.length },
    { key: 'payments', label: 'Payments', count: data.payments.length },
    ...(data.hostProfile ? [{ key: 'host' as TabKey, label: 'Host' }] : []),
    { key: 'chats', label: 'Chats', count: data.conversations.length + data.portraits.length },
    { key: 'notes', label: 'Notes' },
    { key: 'activity', label: 'Activity' },
  ] : [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <aside
        role="dialog"
        aria-label="User details"
        className="bg-app-card border-l border-app-border shadow-xl w-full sm:max-w-xl h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b border-app-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-14 h-14 rounded-full overflow-hidden avatar-host flex items-center justify-center text-cream text-lg font-semibold shrink-0">
                {p?.avatar_url ? (
                  <img src={p.avatar_url as string} alt="" className="w-full h-full object-cover" />
                ) : (
                  ((p?.full_name as string) ?? '?').charAt(0).toUpperCase()
                )}
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-app-fg truncate">{loading ? 'Loading…' : ((p?.full_name as string) ?? 'No name')}</h2>
                <p className="text-xs text-app-muted truncate">{sum?.email ?? (loading ? '' : 'No email')}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {roles.map((r) => <Badge key={r} color={r === 'admin' ? 'red' : r === 'host' ? 'yellow' : 'gray'}>{r}</Badge>)}
                  {Boolean(p?.deleted_at) && <Badge color="red">Deleted</Badge>}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-app-muted hover:text-app-fg hover:bg-black/5 transition-colors shrink-0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {tabs.length > 0 && (
            <div role="tablist" className="flex gap-1 mt-3 -mb-3 overflow-x-auto">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? 'border-gold text-app-fg font-medium' : 'border-transparent text-app-muted hover:text-app-fg'}`}
                >
                  {t.label}{t.count ? <span className="ml-1.5 text-[11px] text-app-muted">{t.count}</span> : null}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {loading || !data ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : (
            <>
              {tab === 'overview' && (
                <>
                  {alerts.length > 0 && (
                    <div className="mb-5 flex flex-col gap-1.5">
                      {alerts.map((a) => (
                        <p key={a.text} className={`text-sm rounded-lg px-3 py-2 border ${a.tone === 'bad' ? 'border-crimson/30 bg-crimson/10 text-crimson' : 'border-gold/30 bg-gold/10 text-app-fg'}`}>
                          {a.text}
                        </p>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5">
                    <Tile label="Trips" value={sum ? sum.trips : '—'} />
                    <Tile label="Open now" value={sum ? sum.open_trips : '—'} />
                    <Tile label="Paid" value={sum ? centsToEur(sum.paid_cents) : '—'} />
                    <Tile label="Last seen" value={sum ? lastSeenLabel(sum.last_sign_in_at) : '—'} />
                    <Tile label="2FA" value={sum ? (sum.two_fa ? 'On' : 'Off') : '—'} />
                  </div>
                  <Section title="Account">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Joined" value={formatDate(p?.created_at as string)} />
                      <Field label="Last updated" value={formatDate(p?.updated_at as string)} />
                      <Field label="Terms accepted" value={p?.terms_accepted_at ? formatDate(p.terms_accepted_at as string) : 'Not yet'} />
                      {Boolean(p?.deleted_at) && <Field label="Deleted at" value={formatDate(p?.deleted_at as string)} />}
                      {(p?.latitude != null) && (
                        <Field label="Location" value={`${(p.latitude as number).toFixed(4)}, ${(p.longitude as number).toFixed(4)}`} />
                      )}
                      <div className="col-span-2"><Field label="User ID" value={<span className="font-mono text-[11px]">{userId}</span>} /></div>
                    </div>
                  </Section>
                  <Section title="Tags">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {(sum?.tags ?? []).map((t) => (
                        <span key={t} className="inline-flex items-center gap-1 text-xs rounded-full pl-2.5 pr-1 py-0.5 bg-app-bg border border-app-border text-app-fg">
                          #{t}
                          <button aria-label={`Remove tag ${t}`} onClick={() => void changeTags([], [t])} className="w-4 h-4 rounded-full text-app-muted hover:text-crimson">×</button>
                        </span>
                      ))}
                      {(sum?.tags ?? []).length === 0 && <span className="text-sm text-app-muted">No tags.</span>}
                    </div>
                    {(sum?.tags ?? []).length < MAX_TAGS_PER_PERSON && (
                      <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); submitTags(); }}>
                        <input
                          aria-label="Add a tag"
                          value={tagText}
                          onChange={(e) => setTagText(e.target.value)}
                          placeholder="Add a tag, e.g. vip"
                          className="flex-1 bg-app-bg border border-app-border rounded-lg px-3 py-1.5 text-sm text-app-fg"
                        />
                        <Button size="sm" variant="secondary" type="submit" disabled={!tagText.trim()}>Add</Button>
                      </form>
                    )}
                    {tagError && <p role="alert" className="mt-1 text-xs text-crimson">{tagError}</p>}
                  </Section>
                  {!p?.deleted_at && (
                    <Section title="Actions">
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setEditingRoles(true)}>Change roles</Button>
                        <Button size="sm" variant="secondary" onClick={() => void runAction('password_reset')} disabled={acting !== null || !sum?.email}>
                          {acting === 'password_reset' ? <Spinner size="sm" /> : 'Send password reset'}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => void runAction('resend_confirmation')} disabled={acting !== null || !sum?.email}>
                          {acting === 'resend_confirmation' ? <Spinner size="sm" /> : 'Resend confirmation'}
                        </Button>
                        {sum?.two_fa && !confirmReset && (
                          <Button size="sm" variant="secondary" onClick={() => setConfirmReset(true)} disabled={acting !== null}>Reset 2FA</Button>
                        )}
                      </div>
                      {confirmReset && (
                        <div role="alert" className="mt-2 rounded-lg border border-gold/30 bg-gold/10 p-3 text-sm text-app-fg flex flex-col gap-2">
                          <p>This removes their authenticator app, so they can sign in with the password alone again. Do it only if they have lost their phone and you are sure it is them. They will be told by email.</p>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => void runAction('reset_2fa')} disabled={acting !== null}>
                              {acting === 'reset_2fa' ? <Spinner size="sm" className="text-ink" /> : 'Reset their 2FA'}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setConfirmReset(false)} disabled={acting !== null}>Cancel</Button>
                          </div>
                        </div>
                      )}
                      {actionNote && (
                        <p role={actionNote.tone === 'bad' ? 'alert' : 'status'} className={`mt-2 text-sm ${actionNote.tone === 'bad' ? 'text-crimson' : 'text-emerald-500'}`}>{actionNote.text}</p>
                      )}
                      <p className="mt-2 text-xs text-app-muted">The emails go to the person's own address. You never see a link or code.</p>
                    </Section>
                  )}
                </>
              )}

              {tab === 'notes' && (
                <>
                  <p className="text-xs text-app-muted mb-2">Private to admins. The person never sees these.</p>
                  <form className="mb-4 flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); if (noteText.trim()) void addNote(); }}>
                    <textarea
                      aria-label="New note"
                      rows={3}
                      maxLength={2000}
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="What should the next admin know?"
                      className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg"
                    />
                    <div className="flex items-center gap-3">
                      <Button size="sm" type="submit" disabled={noteBusy || !noteText.trim()}>Add note</Button>
                      {noteError && <span role="alert" className="text-xs text-crimson">{noteError}</span>}
                    </div>
                  </form>
                  {notes === null ? (
                    <div className="flex justify-center py-6"><Spinner /></div>
                  ) : notes.length === 0 ? (
                    <p className="text-sm text-app-muted">No notes yet.</p>
                  ) : (
                    <ul className="flex flex-col gap-2">
                      {notes.map((n) => (
                        <li key={n.id} className="bg-app-bg rounded-lg px-3 py-2.5">
                          <p className="text-sm text-app-fg whitespace-pre-wrap break-words">{n.body}</p>
                          <div className="mt-1 flex items-center justify-between">
                            <p className="text-[11px] text-app-muted">{n.author_name ?? 'An admin'} · {formatDate(n.created_at)}</p>
                            <button onClick={() => void deleteNote(n.id)} disabled={noteBusy} className="text-[11px] text-app-muted hover:text-crimson">Delete</button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              {tab === 'activity' && (
                audit === null ? (
                  <div className="flex justify-center py-8"><Spinner /></div>
                ) : audit.length === 0 ? (
                  <p className="text-sm text-app-muted">Nothing recorded for this person yet.</p>
                ) : (
                  <ol className="flex flex-col gap-2">
                    {audit.map((a) => (
                      <li key={a.id} className="bg-app-bg rounded-lg px-3 py-2.5">
                        <p className="text-sm text-app-fg">{describeAudit(a)}</p>
                        <p className="text-[11px] text-app-muted mt-0.5">{a.admin_name ?? 'An admin'} · {formatDate(a.created_at)}</p>
                      </li>
                    ))}
                  </ol>
                )
              )}

              {tab === 'trips' && (
                <>
                  <Section title={`Matches (${data.matches.length})`} empty={data.matches.length === 0}>
                    <div className="flex flex-col gap-1.5">
                      {data.matches.map((m) => {
                        const role = m._role as string;
                        const other = (m.profiles as Record<string, unknown> | null)?.full_name as string;
                        return (
                          <div key={m.id as string} className="flex items-center gap-3 bg-app-bg rounded-lg px-3 py-2">
                            <Badge color={statusColor(String(m.status))}>{String(m.status)}</Badge>
                            <div className="flex-1 min-w-0">
                              <span className="text-xs text-app-fg truncate block">
                                {role === 'traveler' ? 'Host' : 'Traveler'}: {other ?? '—'}
                              </span>
                              {typeof m.match_note === 'string' && m.match_note && <p className="text-[10px] text-app-muted truncate">{m.match_note}</p>}
                            </div>
                            {m.rank != null && <span className="text-[10px] text-app-muted shrink-0">#{String(m.rank)}</span>}
                            <span className="text-[10px] text-app-muted shrink-0">{formatDate(m.created_at as string)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </Section>
                  {data.matches.length === 0 && <p className="text-sm text-app-muted">No trips yet.</p>}
                </>
              )}

              {tab === 'payments' && (
                <>
                  <Section title={`Payments (${data.payments.length})`} empty={data.payments.length === 0}>
                    <div className="flex flex-col gap-1.5">
                      {data.payments.map((pay) => (
                        <div key={pay.id as string} className="flex items-center gap-3 bg-app-bg rounded-lg px-3 py-2">
                          <Badge color={statusColor(pay.status as string)}>{pay.status as string}</Badge>
                          <div className="flex-1 min-w-0 flex items-center gap-3">
                            <span className="text-sm font-medium text-app-fg">{centsToEur(pay.amount_cents)}</span>
                            <span className="text-[10px] text-app-muted">as {pay._role as string}</span>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="text-[10px] text-app-muted block">{centsToEur(pay.commission_cents)} commission</span>
                            <span className="text-[10px] text-app-muted block">{centsToEur(pay.host_payout_cents)} to host</span>
                          </div>
                          <span className="text-[10px] text-app-muted shrink-0">{formatDate(pay.created_at as string)}</span>
                        </div>
                      ))}
                    </div>
                  </Section>
                  {data.payments.length === 0 && <p className="text-sm text-app-muted">No payments yet.</p>}
                </>
              )}

              {tab === 'host' && data.hostProfile && (
                <>
                  <Section title="Host profile">
                    {(() => {
                      const h = data.hostProfile!;
                      return (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          <Field label="Headline" value={h.headline as string} />
                          <Field label="City" value={h.city as string} />
                          <Field label="Country" value={h.country as string} />
                          <Field label="Birth country" value={h.birth_country as string} />
                          <Field label="Active" value={h.active ? <Badge color="green">Yes</Badge> : <Badge color="red">No</Badge>} />
                          <Field label="Response time" value={h.response_time as string} />
                          <Field label="Specialties" value={(h.specialties as string[] | null)?.join(', ')} />
                          <div className="col-span-2 sm:col-span-3"><Field label="Bio" value={h.bio as string} /></div>
                          <div className="col-span-2 sm:col-span-3"><Field label="Taste notes" value={h.taste_notes as string} /></div>
                          {typeof h.video_url === 'string' && h.video_url && (
                            <div className="col-span-2 sm:col-span-3">
                              <span className="text-[10px] text-app-muted uppercase tracking-wide">Intro video</span>
                              <video src={h.video_url} controls preload="metadata" className="mt-1 w-full max-w-xs h-40 rounded-lg bg-black object-contain" />
                            </div>
                          )}
                          {(h.moodboard_images as string[])?.length > 0 && (
                            <div className="col-span-2 sm:col-span-3">
                              <span className="text-[10px] text-app-muted uppercase tracking-wide">Moodboard</span>
                              <div className="flex gap-2 mt-1 overflow-x-auto">
                                {(h.moodboard_images as string[]).map((url, i) => (
                                  <img key={i} src={url} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                                ))}
                              </div>
                            </div>
                          )}
                          <Field label="Host agreement accepted" value={h.host_agreement_accepted_at ? formatDate(h.host_agreement_accepted_at as string) : 'Not yet'} />
                          <HostCitiesEditor hostId={userId} />
                        </div>
                      );
                    })()}
                  </Section>

                  <Section title="Stripe Connect" empty={!data.stripeConnect}>
                    {data.stripeConnect && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <Field label="Account ID" value={<span className="font-mono text-[11px]">{data.stripeConnect.stripe_account_id as string}</span>} />
                        <Field label="Onboarding" value={data.stripeConnect.onboarding_complete ? <Badge color="green">Complete</Badge> : <Badge color="yellow">Pending</Badge>} />
                        <Field label="Charges" value={data.stripeConnect.charges_enabled ? <Badge color="green">Enabled</Badge> : <Badge color="red">Disabled</Badge>} />
                        <Field label="Payouts" value={data.stripeConnect.payouts_enabled ? <Badge color="green">Enabled</Badge> : <Badge color="red">Disabled</Badge>} />
                      </div>
                    )}
                  </Section>

                  <Section title={`Availability (${data.availability.length} blocked)`} empty={data.availability.length === 0}>
                    <div className="flex flex-wrap gap-2">
                      {data.availability.map((a, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 bg-crimson/10 text-crimson text-xs px-2.5 py-1 rounded-lg">
                          <span className="w-1.5 h-1.5 rounded-full bg-crimson" />
                          {a.start_date as string} → {a.end_date as string}
                        </span>
                      ))}
                    </div>
                  </Section>
                </>
              )}

              {tab === 'chats' && (
                <>
                  <Section title={`Conversations (${data.conversations.length})`} empty={data.conversations.length === 0}>
                    <div className="flex flex-col gap-1.5">
                      {data.conversations.map((c) => (
                        <div key={c.id as string} className="flex items-center justify-between bg-app-bg rounded-lg px-3 py-2">
                          <span className="font-mono text-[10px] text-app-muted">{(c.id as string).slice(0, 8)}</span>
                          <Badge color={statusColor(c.status as string)}>{c.status as string}</Badge>
                          <span className="text-[10px] text-app-muted">{formatDate(c.created_at as string)}</span>
                        </div>
                      ))}
                    </div>
                  </Section>
                  <Section title={`Taste portraits (${data.portraits.length})`} empty={data.portraits.length === 0}>
                    <div className="flex flex-col gap-2">
                      {data.portraits.map((t) => {
                        const pj = t.portrait_json as Record<string, unknown> | null;
                        return (
                          <div key={t.id as string} className="bg-app-bg rounded-lg px-3 py-2.5">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="font-mono text-[10px] text-app-muted">{(t.id as string).slice(0, 8)}</span>
                              <span className="text-[10px] text-app-muted">{formatDate(t.created_at as string)}</span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <Field label="Trip days" value={t.trip_days as number} />
                              <Field label="Start date" value={t.start_date as string} />
                              <Field label="Pace" value={pj?.pace as string} />
                            </div>
                            {Array.isArray(pj?.interests) && (
                              <div className="mt-1.5">
                                <span className="text-[10px] text-app-muted">Interests: </span>
                                <span className="text-xs text-app-fg">{(pj.interests as string[]).join(', ')}</span>
                              </div>
                            )}
                            {typeof t.host_summary === 'string' && t.host_summary && (
                              <p className="text-xs text-app-muted mt-1.5 line-clamp-2">{t.host_summary}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Section>
                  {data.conversations.length === 0 && data.portraits.length === 0 && <p className="text-sm text-app-muted">No chats yet.</p>}
                </>
              )}
            </>
          )}
        </div>
      </aside>
      {editingRoles && data && (
        <RolesDialog
          userId={userId}
          name={(p?.full_name as string) ?? 'No name'}
          roles={roles as UserRole[]}
          isSelf={me?.id === userId}
          onClose={() => setEditingRoles(false)}
          onSaved={() => { setEditingRoles(false); setReload((n) => n + 1); onChanged?.(); }}
        />
      )}
    </div>
  );
}
